import { useEffect, useState } from "react";

import { IconButton } from "./icon-button";
import { MuiKeyboardArrowUpIcon } from "./mui-arrow-icons";

// A floating "Scroll to top" button — the donor's `ScrollToTopButton`
// (`SpecDetailsIsland/components/MainContent/ScrollToTopButton.tsx`), shared
// because the Editor floats the same one over its own scroll region (#695).
//
// It watches a scroll container — the donor's `scrollRef.current || window` —
// and shows itself once that has scrolled more than 100px, by toggling
// `display` with no transition (`:11-26`); a press smooth-scrolls it back to
// the top (`:28-35`).
//
// The drawing is the donor's inline style (`:38-72`) over MUI's `IconButton` —
// the shared `IconButton` here, so it keeps the centred press ripple: fixed
// 50px in from the bottom right, 40×40, round, white — on hover too, because an
// inline background beats MUI's hover — with a `1px solid rgba(0,0,0,0.54)`
// ring and a `0 2px 4px rgba(0,0,0,0.2)` shadow; the small `KeyboardArrowUp`
// inside. The caption is *inside* the button, absolutely placed with its bottom
// 20px below the button's, so pressing the caption scrolls too: `body1` at
// 0.725rem in `rgba(0,0,0,0.54)`, unwrapped.
//
// Its layer is translated, not copied (#554 D2). The donor's `zIndex: 999`
// (`:54`) means something only on MUI's scale: over the page's own content —
// the header stack at 9, the sticky group headers at 2 — and under the drawer
// (1200), the app bar (1201), the modal layer (1300), snackbars (1400) and
// tooltips (1500), so an open dialog's backdrop dims it with the page. Atlas's
// overlays start at `z-40` (the drawer) and its page content stops at `z-10`,
// so `z-30` keeps that order; the literal 999 would paint over every one of
// them. The deep link's full-screen spinner is the one page layer above it
// on both sides (9999, `spec-lines-body.tsx`).

const THRESHOLD_PX = 100;

type ScrollTarget = HTMLElement | Window;

const scrollTopOf = (target: ScrollTarget) =>
  target instanceof Window ? target.scrollY : target.scrollTop;

interface ScrollToTopButtonProps {
  /** The scroll container; the window when there is none yet. */
  target: HTMLElement | null;
}

export function ScrollToTopButton({ target }: ScrollToTopButtonProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const scroller: ScrollTarget = target ?? window;
    const onScroll = () => setVisible(scrollTopOf(scroller) > THRESHOLD_PX);
    // Read once as it attaches too: a region already scrolled — the router's
    // scroll restoration sets the offset before any listener — shows the
    // button without waiting for the next scroll.
    onScroll();
    scroller.addEventListener("scroll", onScroll);
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [target]);

  return (
    <IconButton
      aria-label="Scroll to top"
      onClick={() => (target ?? window).scrollTo({ top: 0, behavior: "smooth" })}
      style={{ display: visible ? "flex" : "none" }}
      className="text-lennar-action-active border-lennar-action-active shadow-scroll-to-top fixed right-[50px] bottom-[50px] z-30 size-10 border border-solid bg-white p-2 hover:bg-white"
    >
      <MuiKeyboardArrowUpIcon className="size-5" />
      <span className="absolute bottom-[-20px] text-center text-[0.725rem] leading-normal whitespace-nowrap">
        Scroll to top
      </span>
    </IconButton>
  );
}
